import React from 'react';
import { Loader2, X } from 'lucide-react';
import { money } from './simMath';
import { TrailControls } from './TrailControls';

const fmt = (o, v) => (v == null ? '—' : Number(v).toFixed(o.pair?.includes('JPY') ? 3 : 5));
const signed = (v) => `${v > 0 ? '+' : v < 0 ? '-' : ''}${money(Math.abs(v))}`;
const tone = (v) => (v > 0 ? 'text-emerald-400' : v < 0 ? 'text-rose-400' : 'text-slate-300');

const tagClass = (o, byOutcome) => {
  const good = byOutcome ? (o.realized_pnl || 0) >= 0 : o.direction === 'BUY';
  return good ? 'sim-tag-buy' : 'sim-tag-sell';
};

const Head = ({ o, label, byOutcome }) => (
  <div className="flex items-center gap-3">
    <span className={`sim-tag ${tagClass(o, byOutcome)}`}>{label || `${o.direction} STOP`}</span>
    <span className="font-semibold text-white">{o.pair}</span>
    <span className="sim-mono text-xs text-slate-500">{o.lot_size} lot</span>
  </div>
);

const Sl = ({ o }) => (
  <span data-testid={`sl-value-${o.order_id}`}>
    SL <b className="text-rose-400">{fmt(o, o.sl_price)}</b>
    {o.sl_trailed && <span className="sim-trailed" data-testid={`sl-trailed-badge-${o.order_id}`}>TRAILED</span>}
  </span>
);

export const PendingRow = ({ o, onCancel, onAuto, onMoveSl, busy }) => (
  <div className="sim-row" data-testid={`position-row-${o.order_id}`}>
    <Head o={o} />
    <div className="sim-mono grid grid-cols-2 gap-x-5 gap-y-1 text-xs text-slate-400 sm:grid-cols-4">
      <span>Entry <b className="text-sky-300">{fmt(o, o.entry_price)}</b></span>
      <Sl o={o} />
      <span>TP <b className="text-emerald-400">{fmt(o, o.tp_price)}</b></span>
      <span>Risk <b className="text-white">{money(o.actual_risk ?? o.risk_amount)}</b></span>
    </div>
    <button type="button" onClick={() => onCancel(o)} disabled={busy === o.order_id} className="sim-cancel" data-testid={`cancel-order-${o.order_id}`}>
      <X size={14} /> {busy === o.order_id ? 'Cancelling…' : 'Cancel'}
    </button>
    <TrailControls o={o} onAuto={onAuto} onMoveSl={onMoveSl} busy={busy} />
  </div>
);

export const LiveRow = ({ o, onClose, onAuto, onMoveSl, busy }) => {
  const pnl = o.floating_pnl || 0;
  return (
    <div className="sim-row sim-row-live" data-testid={`live-row-${o.order_id}`}>
      <Head o={o} label={`${o.direction} · LIVE`} />
      <div className="sim-mono grid grid-cols-2 gap-x-5 gap-y-1 text-xs text-slate-400 sm:grid-cols-4">
        <span>Entry <b className="text-sky-300">{fmt(o, o.entry_price)}</b></span>
        <span>Now <b className="text-white" data-testid={`live-price-${o.order_id}`}>{fmt(o, o.current_price)}</b></span>
        <Sl o={o} />
        <span>TP <b className="text-emerald-400">{fmt(o, o.tp_price)}</b></span>
      </div>
      <div className="flex items-center gap-3">
        <span className={`sim-mono text-lg font-semibold ${tone(pnl)}`} data-testid={`live-pnl-${o.order_id}`}>{signed(pnl)}</span>
        <button type="button" onClick={() => onClose(o)} disabled={busy === o.order_id} className="sim-close" data-testid={`close-trade-${o.order_id}`}>
          {busy === o.order_id ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />} Close
        </button>
      </div>
      <TrailControls o={o} onAuto={onAuto} onMoveSl={onMoveSl} busy={busy} />
    </div>
  );
};

const REASON = { TP: 'TP HIT', SL: 'SL HIT', MANUAL: 'CLOSED' };
const reasonLabel = (o) => (o.close_reason === 'SL' && o.sl_trailed ? 'SL HIT (TRAILED)' : REASON[o.close_reason] || 'CLOSED');

export const ClosedRow = ({ o }) => {
  const cancelled = o.status === 'CANCELLED';
  return (
    <div className={`sim-row ${cancelled ? 'opacity-60' : ''}`} data-testid={`closed-row-${o.order_id}`}>
      <Head o={o} label={cancelled ? 'CANCELLED' : reasonLabel(o)} byOutcome={!cancelled} />
      <div className="sim-mono grid grid-cols-2 gap-x-5 gap-y-1 text-xs text-slate-400 sm:grid-cols-3">
        <span>Entry <b className="text-sky-300">{fmt(o, o.entry_price)}</b></span>
        <span>Exit <b className="text-white">{cancelled ? '—' : fmt(o, o.exit_price)}</b></span>
        <span>{new Date(o.closed_at || o.cancelled_at || o.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
      </div>
      {!cancelled && <span className={`sim-mono text-base font-semibold ${tone(o.realized_pnl)}`} data-testid={`closed-pnl-${o.order_id}`}>{signed(o.realized_pnl || 0)}</span>}
    </div>
  );
};
